import React, { useState, useEffect, Suspense, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { PouchDBProvider } from './components/Utils/PouchDBProvider';
import PouchDBStatus from './components/Utils/PouchDBStatus';
import Header from './components/pages/Header';
import MobileBottomNav from './components/MobileBottomNav/MobileBottomNav';
import WhatsAppFloat from './components/WhatsAppChat/WhatsAppFloat';
import InstallBanner from './components/InstallPrompt/InstallBanner';
import NotificationPrompt from './components/InstallPrompt/NotificationPrompt';
import { updateDBHeaders } from './db/direct-couchdb';
import { initializeTimezone } from './utils/timezone-config';
// import Footer from './components/pages/Footer';
import Home from './components/pages/Home';
import About from './components/pages/About';
import Contact from './components/pages/Contact';
import LoginRegisterContainer from './components/LoginRegisterContainer/LoginRegisterContainer';
import CustomerContainer from './components/CustomerContainer/CustomerContainer';
import AdminContainer from './components/Admin/AdminContainer';
import ShoppingCart from './components/ShopingCart/ShoppingCart';
import CustomerOrderList from './components/Orders/CustomerOrderList';
import PaymentSuccessHandler from './components/Orders/PaymentSuccessHandler';
import SubscriptionPlans from './components/Subscription/SubscriptionPlans';
import SubscriptionCustomizer from './components/Subscription/SubscriptionCustomizer';
import SubscriptionCheckout from './components/Subscription/SubscriptionCheckout';
import ProductDetails from './components/ProductDetails/ProductDetails';
import PlantGallery from './components/PlantGallery/PlantGallery';
import './App.scss';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const timeoutRef = useRef(null);
  
  const TIMEOUT_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds

  // Check if user is already authenticated from session storage or localStorage
  useEffect(() => {
    // Initialize timezone on app load
    initializeTimezone();
    
    const userAuthenticated = sessionStorage.getItem("isUserAuthenticated") === "true";
    const userIsAdmin = sessionStorage.getItem("isAdmin") === "true";
    const userId = sessionStorage.getItem("customerId");
    
    // If session storage is empty, check localStorage for token
    if (!userAuthenticated) {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (token && user) {
        try {
          const userData = JSON.parse(user);
          // Restore session from localStorage
          sessionStorage.setItem("isUserAuthenticated", "true");
          sessionStorage.setItem("isAdmin", userData.role === 'admin' ? "true" : "false");
          sessionStorage.setItem("customerId", userData.id);
          sessionStorage.setItem("customerEmail", userData.email);
          sessionStorage.setItem("customerName", userData.name);
          if (userData.phone) sessionStorage.setItem("customerPhone", userData.phone);
          
          // Update PouchDB headers with the token
          updateDBHeaders();
          
          setIsAuthenticated(true);
          setIsAdmin(userData.role === 'admin');
          setCustomerId(userData.id);
          return;
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
    } else {
      // Session exists, ensure PouchDB headers are updated
      updateDBHeaders();
    }
    
    setIsAuthenticated(userAuthenticated);
    setIsAdmin(userIsAdmin);
    setCustomerId(userId);
  }, []);

  const setUserAuthenticatedStatus = (isAdminUser, userId) => {
    setIsAuthenticated(true);
    setIsAdmin(isAdminUser);
    setCustomerId(userId);
  };

  const handleLogout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear session storage
    sessionStorage.removeItem("isUserAuthenticated");
    sessionStorage.removeItem("isAdmin");
    sessionStorage.removeItem("customerId");
    sessionStorage.removeItem("customerEmail");
    sessionStorage.removeItem("customerName");
    sessionStorage.removeItem("jwt_token");
    sessionStorage.removeItem("jwt_refresh_token");
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset state
    setIsAuthenticated(false);
    setIsAdmin(false);
    setCustomerId(null);
    
    // Redirect to home page
    window.location.href = "/";
  }, []);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (isAuthenticated) {
      timeoutRef.current = setTimeout(() => {
        alert('Session expired due to inactivity. You will be logged out.');
        handleLogout();
      }, TIMEOUT_DURATION);
    }
  }, [isAuthenticated, handleLogout, TIMEOUT_DURATION]);

  // Set up activity listeners
  useEffect(() => {
    if (isAuthenticated) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      
      const resetTimeoutHandler = () => resetTimeout();
      
      // Add event listeners
      events.forEach(event => {
        document.addEventListener(event, resetTimeoutHandler, true);
      });
      
      // Start the timeout
      resetTimeout();
      
      // Cleanup
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, resetTimeoutHandler, true);
        });
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [isAuthenticated, resetTimeout]);

  return (
    <PouchDBProvider>
      <Router>
        <div className="App">
          <Header isUserAuthenticated={isAuthenticated} handleLogout={handleLogout} isAdmin={isAdmin} />
          {isAdmin && (
            <div className="sync-status-container">
              <PouchDBStatus />
            </div>
          )}
          <main className="content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route 
                path="/login" 
                element={
                  isAuthenticated ? 
                  <Navigate to={isAdmin ? "/admin" : "/"} /> :
                  <LoginRegisterContainer 
                    setUserAuthenticatedStatus={setUserAuthenticatedStatus} 
                  />
                } 
              />
              <Route 
                path="/customer/*" 
                element={
                  isAuthenticated && !isAdmin ? 
                  <CustomerContainer customerId={customerId} /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/admin/*" 
                element={
                  isAuthenticated && isAdmin ? 
                  <AdminContainer /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/cart" 
                element={
                  isAuthenticated ? 
                  <ShoppingCart 
                    customerId={customerId} 
                    standalone={true}
                  /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/orders" 
                element={
                  isAuthenticated ? 
                  <CustomerOrderList /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/payment/success" 
                element={<PaymentSuccessHandler />} 
              />
              <Route 
                path="/subscriptions" 
                element={<SubscriptionPlans />} 
              />
              <Route 
                path="/subscription/customize/:planType" 
                element={
                  isAuthenticated ? 
                  <SubscriptionCustomizer /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/subscription/checkout" 
                element={
                  isAuthenticated ? 
                  <SubscriptionCheckout /> : 
                  <Navigate to="/login" />
                } 
              />
              <Route 
                path="/product/:productId" 
                element={<ProductDetails />} 
              />
              <Route 
                path="/plants" 
                element={<PlantGallery />} 
              />
            </Routes>
          </main>
          <InstallBanner />
          <NotificationPrompt />
          <MobileBottomNav />
          <WhatsAppFloat />
          {/* <Footer /> */}
        </div>
      </Router>
    </PouchDBProvider>
  );
}

export default App;